<template>
  <div>
    <!--기본정보-->
    <field-select-module v-model="form.stdInfo" :board="board" :is-print="isPrint" :vislbleAlert="true" :options="[{ type: 'alert', label: '알림 대상자 선택' }]" />

    <!-- 구분 -->
    <div class="mt-28px">
      <h5 class="my-4"><i class="dot"></i> 구분</h5>
      <div class="flex flex-col mb-10 xl:grid grid-cols-8 gap-x-6 gap-y-4 mt-6 xl:mb-0">
        <fragment v-for="option in ['사업주', '근로자', '기타']" :key="`요청자구분_${option}`">
          <PrintRadio v-if="isPrint" :value="form.요청자구분 === option" :label="option" />
          <vs-radio v-else v-model="form.요청자구분" vs-name="요청자구분" :vs-value="option">{{ option }}</vs-radio>
        </fragment>
      </div>
    </div>

    <!-- 대상 -->
    <div class="mt-28px">
      <h5 class="my-4"><i class="dot"></i> 대상</h5>
      <div class="flex flex-col mb-10 xl:grid grid-cols-6 gap-x-6 gap-y-4 mt-6 xl:mb-0">
        <fragment v-for="option in ['도급인 순회점검', '산업안전보건위원회', '설비안전위원회', '크레인안전협의체', '안전보건협의체', '합동안전점검']" :key="`대상_${option}`">
          <PrintCheckbox v-if="isPrint" :label="option" :value="form.대상.includes(option)" />
          <vs-checkbox v-else v-model="form.대상" vs-name="대상" :vs-value="option">
            {{ option }}
          </vs-checkbox>
        </fragment>
        <div class="flex items-center justify-center gap-1">
          <PrintCheckbox v-if="isPrint" label="기타" :value="form.대상.includes('기타')" />
          <vs-checkbox v-else v-model="form.대상" vs-name="대상" vs-value="기타" @change="() => (form.대상기타 = '')">기타</vs-checkbox>
          <p v-if="isPrint" class="print-input flex-1">{{ form.대상기타 }}</p>
          <ml-input v-else :readonly="!form.대상.includes('기타')" class="flex-1" v-model="form.대상기타" />
        </div>
      </div>
    </div>

    <!-- 유해위험요인 점검(개선) 요청 -->
    <div class="mt-28px">
      <h5 class="my-4"><i class="dot"></i> 유해위험요인 점검(개선) 요청</h5>
      <div class="writeGridContainer">
        <div>
          <div>소속명</div>
          <p v-if="isPrint" class="print-input">{{ form.소속명 }}</p>
          <ml-input v-else readonly :isPrint="isPrint" v-model="form.소속명" :max="300" :visible-counter="true" />
        </div>
        <div>
          <div>부서명</div>
          <p v-if="isPrint" class="print-input">{{ form.부서명 }}</p>
          <ml-input v-else readonly :isPrint="isPrint" v-model="form.부서명" :max="300" :visible-counter="true" />
        </div>
        <div>
          <div>요청자</div>
          <p v-if="isPrint" class="print-input">{{ form.요청자 }}</p>
          <ml-input v-else readonly :isPrint="isPrint" v-model="form.요청자" :max="300" :visible-counter="true" />
        </div>
        <div>
          <div>점검(개선) 대상</div>
          <p v-if="isPrint" class="print-input">{{ form.점검대상.com_name }}</p>
          <virtual-company-selector v-else class="w-full"
                                    v-model="form.점검대상.id"
                                    :company-name="form.점검대상.com_name"
                                    :readonly="board.readonly"
                                    @select="(company) => form.점검대상 = company"/>
        </div>
        <div>
          <div>재해형태</div>
          <p v-if="isPrint" class="print-input">{{ form.재해형태 }}</p>
          <disaster-class-selector v-else v-model="form.재해형태" :readonly="board.readonly" />
        </div>
        <div>
          <div>장소</div>
          <p v-if="isPrint" class="print-input">{{ form.장소 }}</p>
          <ml-input v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.장소" :max="300" :visible-counter="true" />
        </div>

        <div class="writeGridLineItem">
          <div>점검(개선) 대상 사유</div>
          <p v-if="isPrint" class="print-input">{{ form.대상사유 }}</p>
          <ml-textarea v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.대상사유" />
        </div>
        <div>
          <div>요청일</div>
          <p v-if="isPrint" class="print-input">{{ form.요청일 }}</p>
          <date-selector v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.요청일"/>
        </div>
        <div>
          <div>조치기한</div>
          <p v-if="isPrint" class="print-input">{{ form.조치기한 }}</p>
          <date-selector v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.조치기한" :max="100" :visible-counter="true" :style="{ color: isOverdueAndNotCompleted ? '#E42728' : '' }" />
        </div>
        <div>
          <div>조치부서</div>
          <p v-if="isPrint" class="print-input">{{ form.조치요청부서 }}</p>
          <ml-input v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.조치요청부서" :max="300" :visible-counter="true" />
        </div>
      </div>
    </div>

    <!--조치 전 사진-->
    <photo-upload-grid-module :row-count="2" :grid-count="2" v-model="form.photoList1" :is-print="isPrint" :readonly="board.readonly" section-title="조치 전 사진" placeholder="사진 설명" class="mt-28px" />

    <!-- 조치 현황 -->
    <div class="mt-28px">
      <h5 class="my-4"><i class="dot"></i> 조치 현황</h5>
      <div class="writeGridContainer">
        <div>
          <div>소속명</div>
          <p v-if="isPrint" class="print-input">{{ form.조치현황_조치자.company && form.조치현황_조치자.company.com_name?
            form.조치현황_조치자.company.com_name : '' }}</p>
          <ml-input v-else :readonly="true" :isPrint="isPrint"
                    :value="form.조치현황_조치자.company && form.조치현황_조치자.company.com_name?
                     form.조치현황_조치자.company.com_name : ''" :max="300" :visible-counter="true" />
        </div>
        <div>
          <div>부서명</div>
          <p v-if="isPrint" class="print-input">{{ form.조치현황_조치자.section? form.조치현황_조치자.section : '' }}</p>
          <ml-input v-else :readonly="true" :isPrint="isPrint"
                    :value="form.조치현황_조치자.section? form.조치현황_조치자.section : ''" :max="300" :visible-counter="true" />
        </div>
        <div>
          <div>조치자</div>
          <p v-if="isPrint" class="print-input">{{ form.조치현황_조치자.name? form.조치현황_조치자.name : '' }}</p>
          <member-selector v-else class="w-full"
                           popup-type="user"
                           v-model="form.조치현황_조치자.id"
                           :member-name="form.조치현황_조치자.name"
                           @select="(member) => onSelectMember(form.조치현황_조치자, member)"/>
        </div>
        <div>
          <div>조치내용</div>
          <p v-if="isPrint" class="print-input">{{ form.조치현황_조치내용 }}</p>
          <ml-input v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.조치현황_조치내용" :max="300" :visible-counter="true" />
        </div>
        <div>
          <div>조치 현황</div>
          <p v-if="isPrint" class="print-input">{{ form.조치현황_조치현황 }}</p>
          <custom-option-selector v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.조치현황_조치현황" class="w-full" :options="['조치 전', '조치 중', '조치 완료']" />
        </div>
        <div>
          <div>조치 완료일</div>
          <p v-if="isPrint" class="print-input">{{ form.조치현황_조치완료일 }}</p>
          <date-selector v-else :readonly="board.readonly" :isPrint="isPrint" v-model="form.조치현황_조치완료일" :max="100" :visible-counter="true" />
        </div>
      </div>
    </div>

    <!--조치 후 사진-->
    <photo-upload-grid-module :row-count="2" :grid-count="2" v-model="form.photoList2" :is-print="isPrint" :readonly="board.readonly" section-title="조치 후 사진" placeholder="사진 설명" class="mt-28px" />

    <!-- 파일첨부 -->
    <file-upload-module :readonly="board.readonly" v-model="form.fileList" :is-print="isPrint" class="mt-8" />
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import FileUploader from "@/components/FileUploader.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import DateSelectorRang from "@/components/selector/DateSelectorRang";
import NumberInput from "@/components/NumberInput.vue";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector";

import PrintRadio from "@/components/print/PrintRadio.vue";
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";
import CustomOptionSelector from "@/components/selector/CustomOptionSelector";
import MemberSelector from "@/components/selector/MemberSelector.vue";
import VirtualCompanySelector from "@/components/selector/VirtualCompanySelector.vue";

export default {
  components: {
    VirtualCompanySelector,
    MemberSelector,
    NumberInput,
    DateSelector,
    FileUploader,
    FileUploadModule,
    FieldSelectModule,
    DateSelectorRang,
    DisasterClassSelector,

    PrintRadio,
    PrintCheckbox,
    CustomOptionSelector,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
    header: Object,
  },
  data() {
    return {
      form: {
        stdInfo: {},
        fileList: [],

        요청자구분: "",
        대상: [],
        대상기타: "",

        소속명: "",
        부서명: "",
        요청자: "",
        요청일: "",
        점검대상: { id: 0, com_name: "" },
        재해형태: "",
        장소: "",
        대상사유: "",
        조치기한: "",
        조치요청부서: "",

        photoList1: [],

        조치현황_조치자: {},
        조치현황_조치내용: "",
        조치현황_조치현황: "",
        조치현황_조치완료일: "",

        photoList2: [],
      },
    };
  },
  computed: {
    postId() {
      return parseInt(this.$route.params.postId);
    },
    isOverdueAndNotCompleted() {
      const now = new Date();
      const actionDeadline = new Date(this.form.조치기한);
      return this.form.조치현황 !== "조치 완료" && actionDeadline < now;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    board: {
      immediate: true,
      deep: true,
      handler(value) {
        if (!this.postId && value.writer && value.writer.com_name && !this.form.소속명) {
          this.form.소속명 = value.writer.com_name;
          this.form.부서명 = value.writer.section;
          this.form.요청자 = value.writer.name;
        }
      },
    },
  },
  created() {
    const keys = Object.keys(this.form);
    for (const key of keys) {
      if (this.value[key]) {
        this.form[key] = this.value[key];
      }
    }
  },
  methods: {
    // onSelectMember(target, member) {
    //   target.id = member.id;
    //   target.name = member.name;
    // },
    onSelectMember (member, selectedMember) {
      console.log(member, selectedMember);
      const keys = Object.keys(selectedMember);
      for (const key of keys) {
        member[key] = selectedMember[key];
      }


    },
  },
};
</script>
<style lang="scss" scoped></style>
